import React from 'react';
import { CheckBoxParam } from '../../types/CheckBoxParam';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { RequiredSymbol } from '../../partials/RequiredSymbol/RequiredSymbol';
import { Help } from '../../partials/Help/Help';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { Modifier } from '../../../../lib/bemUtils/Modifier';
import bem from '../../../../lib/bemUtils/bem';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type Checkbox = {
  label: React.ReactNode;
  labelInvisible?: boolean;
  helpText?: string;
  param?: CheckBoxParam<string>;
  modifier?: Modifier<'single-checkbox-without-text-label'>;
  ref?: React.Ref<HTMLInputElement | null> | null;
} & Omit<HTMLInputProps, 'id' | 'className' | 'type'>;

/**
 * Convert Backend Checkbox Properties to HTML conform attributes.
 */
function normalizeParamsToHTMLAttributes(htmlInputProps: HTMLInputProps, param?: CheckBoxParam<string>) {
  return {
    name: param?.name,
    required: param?.required,
    defaultValue: param?.value,
    defaultChecked: param?.checked,
    ...htmlInputProps,
  };
}

export function Checkbox({ label, helpText, labelInvisible, modifier, param, ref, ...htmlInputProps }: Checkbox) {
  const id = useHtmlValidId();
  const checkboxId = `checkbox-${id}`;
  const helpTextId = `checkbox-help-text-${id}`;

  const htmlAttributes = normalizeParamsToHTMLAttributes(htmlInputProps, param);

  const labelContent = (
    <>
      {label}
      {htmlAttributes.required && <RequiredSymbol />}
    </>
  );

  return (
    <ElementContainer>
      <FieldWrapper>
        <input
          id={checkboxId}
          ref={ref}
          className="form2-checkbox"
          type="checkbox"
          aria-describedby={helpText && helpTextId}
          {...htmlAttributes}
        />
        <span className={bem('form2-checkbox__label-container', modifier)}>
          <label className={bem('form2-checkbox__label', modifier)} htmlFor={checkboxId}>
            {labelInvisible ? <span className="invisible">{labelContent}</span> : labelContent}
          </label>
        </span>
        {helpText && <Help helpText={helpText} id={helpTextId} />}
      </FieldWrapper>
    </ElementContainer>
  );
}
